import { useState } from "react";
import { C } from "@/brand/tokens";
import { resourceCategories6, pillars6 } from "@/brand/contentF6";

const inputStyle = {
  background: "#FFFFFF",
  border: `1px solid ${C.brassLine}`,
  color: C.textOnLight,
  fontSize: "0.92rem",
};

function Field({ label, children }) {
  return (
    <label className="block">
      <span
        className="block mb-1.5 font-inter text-[10px] uppercase font-semibold"
        style={{ color: C.inkOnLightSoft, letterSpacing: "0.18em" }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}

const EMPTY = {
  title: "",
  category: "insights",
  summary: "",
  body: "",
  featured_image: "",
  author: "Katrina Bernard, JD, PCC",
  publish_date: new Date().toISOString().slice(0, 10),
  featured: false,
  published: true,
  download_url: "",
  download_label: "",
  related_pillar: "",
};

/**
 * ResourceForm — create/edit form for a CMS resource.
 */
export function ResourceForm({ initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState(() => ({ ...EMPTY, ...(initial || {}) }));
  const set = (key) => (e) => {
    const value = e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e;
    setForm((f) => ({ ...f, [key]: value }));
  };

  const submit = (e) => {
    e.preventDefault();
    onSave({
      ...form,
      featured_image: form.featured_image || null,
      download_url: form.download_url || null,
      download_label: form.download_label || null,
      related_pillar: form.related_pillar || null,
    });
  };

  return (
    <form
      onSubmit={submit}
      data-testid="resource-form"
      className="p-8 space-y-6"
      style={{ background: C.surfaceLight2, border: `1px solid ${C.accentMetal}` }}
    >
      <p
        className="font-cormorant"
        style={{ color: C.textOnLight, fontSize: "1.5rem" }}
      >
        {initial?.id ? "Edit resource" : "New resource"}
      </p>

      <div className="grid md:grid-cols-2 gap-5">
        <Field label="Title *">
          <input
            data-testid="form-title"
            required
            value={form.title}
            onChange={set("title")}
            className="w-full px-3.5 py-2.5"
            style={inputStyle}
          />
        </Field>
        <Field label="Category *">
          <select
            data-testid="form-category"
            value={form.category}
            onChange={set("category")}
            className="w-full px-3.5 py-2.5"
            style={inputStyle}
          >
            {resourceCategories6.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <Field label="Summary *">
        <textarea
          data-testid="form-summary"
          required
          rows={2}
          value={form.summary}
          onChange={set("summary")}
          className="w-full px-3.5 py-2.5"
          style={inputStyle}
        />
      </Field>

      <Field label="Full body content * (separate paragraphs with a blank line)">
        <textarea
          data-testid="form-body"
          required
          rows={8}
          value={form.body}
          onChange={set("body")}
          className="w-full px-3.5 py-2.5"
          style={inputStyle}
        />
      </Field>

      <div className="grid md:grid-cols-3 gap-5">
        <Field label="Author">
          <input
            data-testid="form-author"
            value={form.author}
            onChange={set("author")}
            className="w-full px-3.5 py-2.5"
            style={inputStyle}
          />
        </Field>
        <Field label="Publish date">
          <input
            data-testid="form-publish-date"
            type="date"
            value={form.publish_date || ""}
            onChange={set("publish_date")}
            className="w-full px-3.5 py-2.5"
            style={inputStyle}
          />
        </Field>
        <Field label="Related service pillar">
          <select
            data-testid="form-related-pillar"
            value={form.related_pillar || ""}
            onChange={set("related_pillar")}
            className="w-full px-3.5 py-2.5"
            style={inputStyle}
          >
            <option value="">— None —</option>
            {pillars6.map((p) => (
              <option key={p.slug} value={p.slug}>
                {p.label}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <div className="grid md:grid-cols-3 gap-5">
        <Field label="Featured image URL">
          <input
            data-testid="form-featured-image"
            value={form.featured_image || ""}
            onChange={set("featured_image")}
            placeholder="https://…"
            className="w-full px-3.5 py-2.5"
            style={inputStyle}
          />
        </Field>
        <Field label="Download / CTA label">
          <input
            data-testid="form-download-label"
            value={form.download_label || ""}
            onChange={set("download_label")}
            placeholder="Download the guide (PDF)"
            className="w-full px-3.5 py-2.5"
            style={inputStyle}
          />
        </Field>
        <Field label="Download / CTA URL">
          <input
            data-testid="form-download-url"
            value={form.download_url || ""}
            onChange={set("download_url")}
            placeholder="https://…"
            className="w-full px-3.5 py-2.5"
            style={inputStyle}
          />
        </Field>
      </div>

      <div className="flex flex-wrap items-center gap-8">
        <label className="inline-flex items-center gap-2.5 cursor-pointer">
          <input
            data-testid="form-featured"
            type="checkbox"
            checked={form.featured}
            onChange={set("featured")}
          />
          <span
            className="font-inter text-[11px] uppercase font-semibold"
            style={{ color: C.textOnLight, letterSpacing: "0.16em" }}
          >
            Featured
          </span>
        </label>
        <label className="inline-flex items-center gap-2.5 cursor-pointer">
          <input
            data-testid="form-published"
            type="checkbox"
            checked={form.published}
            onChange={set("published")}
          />
          <span
            className="font-inter text-[11px] uppercase font-semibold"
            style={{ color: C.textOnLight, letterSpacing: "0.16em" }}
          >
            Published
          </span>
        </label>
      </div>

      <div className="flex items-center gap-4 pt-2">
        <button
          type="submit"
          data-testid="form-save"
          disabled={saving}
          className="px-6 py-3 font-inter text-[11px] uppercase font-semibold disabled:opacity-60"
          style={{
            background: C.surfacePrimary,
            color: C.textOnDark,
            letterSpacing: "0.2em",
          }}
        >
          {saving ? "Saving…" : "Save resource"}
        </button>
        <button
          type="button"
          data-testid="form-cancel"
          onClick={onCancel}
          className="px-6 py-3 font-inter text-[11px] uppercase font-semibold"
          style={{
            background: "transparent",
            color: C.textOnLight,
            border: `1px solid ${C.brassLine}`,
            letterSpacing: "0.2em",
          }}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
